/**
 * Formatting helpers for the dashboard trend cards.
 *
 * Kept free of React so they can be unit-tested directly — the label-density
 * rule and the null-vs-zero distinction are both easy to get subtly wrong.
 */

/** German decimal formatting, e.g. 9.05 → "9,1" at 1 decimal. */
export function formatDe(value, decimals = 0) {
  if (value == null) return "—";
  return value.toLocaleString("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Whether a month slot shows its label.
 *
 * With 12 months in a ~300px card there is no room for 12 labels at 9.5px, so
 * label the newest month and every third month back from it (indices 11, 8, 5,
 * 2) — the newest is the one the headline number refers to, so it must always be
 * anchored. The hovered slot always labels itself, so hovering never leaves the
 * reader guessing which month they are on.
 *
 * Every slot still *renders* (equal flex), only the text is conditional — that
 * keeps labels aligned under their bars and avoids layout shift on hover.
 */
export function monthLabelVisible(index, length, hovered = null) {
  if (index === hovered) return true;
  return (length - 1 - index) % 3 === 0;
}

/**
 * Bar height as a percentage of the tallest bar, zero-based.
 *
 * Deliberately not the click dummy's "+35 % of range" floor: that formula draws
 * the smallest month at ~26 % height, so for "Überfällige Fälle" a month with
 * zero overdue cases — the whole point of the metric — would render a bar a
 * quarter as tall as the worst month.
 *
 * Returns null for "no data", which the caller must render differently from a
 * measured zero.
 */
export function barHeightPct(value, max) {
  if (value == null) return null;
  if (value === 0) return 0;
  const safeMax = max > 0 ? max : 1;
  return (value / safeMax) * 100;
}

/** The largest value in a series, ignoring gaps. Never 0, so callers can divide. */
export function seriesMax(months = []) {
  const values = months.map((m) => m.value).filter((v) => v != null);
  return values.length > 0 ? Math.max(...values, 0) : 0;
}

/**
 * Month-over-month delta pill text, e.g. "−12 %" / "+8 %" / "±0 %".
 * Uses the Unicode minus (U+2212) like the dummy, not a hyphen.
 */
export function formatDeltaPct(deltaPct) {
  if (deltaPct == null) return null;
  if (deltaPct === 0) return "±0 %";
  return `${deltaPct > 0 ? "+" : "−"}${Math.abs(deltaPct)} %`;
}

/** Fold umlauts so "Gebaude" matches "Gebäude" in the board's search box. */
export function foldUmlauts(value) {
  return (value ?? "")
    .toString()
    .toLowerCase()
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}
